import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  MdArchive,
  MdArrowDownward,
  MdArrowUpward,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdExpandMore,
  MdSearch,
  MdSettings,
  MdUnarchive,
  MdViewColumn,
} from 'react-icons/md';
import postgraduateAPI from '../../api/postgraduateAPI';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken } from '../../utils/hrApiHelpers';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const PAGE_SIZE = 80;
const REPORT_PATH = '/bu-dashboard/postgraduate/student-list';

const FILTER_KEYS = [
  'faculty_id',
  'department_id',
  'batch_id',
  'gender',
  'district',
  'active_state',
];

function stateClass(state) {
  const key = String(state || '').toLowerCase();
  if (key === 'admitted') return 'ea-adm-state--admitted';
  if (key === 'fee_paid') return 'ea-adm-state--fee';
  if (key === 'detain') return 'ea-adm-state--detain';
  if (key === 'cancelled') return 'ea-adm-state--cancelled';
  return 'ea-adm-state--draft';
}

function titleCase(value) {
  const text = String(value || 'active');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function formatCount(n) {
  return Number(n || 0).toLocaleString();
}

export default function PgStudentViewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();

  const filterParams = useMemo(() => {
    const next = {};
    FILTER_KEYS.forEach((key) => {
      const value = searchParams.get(key);
      if (value) next[key] = key.endsWith('_id') ? Number(value) : value;
    });
    if (!next.active_state) next.active_state = 'active';
    return next;
  }, [searchParams]);

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('student_no');
  const [orderDir, setOrderDir] = useState('asc');
  const [titleLabel, setTitleLabel] = useState('All');
  const [selected, setSelected] = useState(() => new Set());
  const [columnsOpen, setColumnsOpen] = useState(false);
  const columnsRef = useRef(null);
  const [visibleCols, setVisibleCols] = useState({
    batch: true,
    faculty: true,
    department: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const actionsRef = useRef(null);

  const listTitle = `Students - ${titleLabel} - (${titleCase(filterParams.active_state)})`;

  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      postgraduateAPI.getUgStudentListReport(
        {
          ...filterParams,
          search: search || undefined,
          order,
          order_dir: orderDir,
          limit: PAGE_SIZE,
          offset,
          include_image: false,
        },
        token
      )
    );
    setLoading(false);
    if (fetchError) {
      setError(fetchError);
      setRecords([]);
      setTotal(0);
      return;
    }
    setRecords(data?.records || []);
    setTotal(data?.student_count ?? data?.total ?? 0);
    setTitleLabel(
      data?.batch?.name || data?.department?.name || data?.faculty?.name || 'All'
    );
    setSelected(new Set());
  }, [ensureHrToken, filterParams, search, order, orderDir, offset]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!columnsOpen && !actionsOpen) return undefined;
    const onDoc = (event) => {
      if (columnsOpen && columnsRef.current && !columnsRef.current.contains(event.target)) {
        setColumnsOpen(false);
      }
      if (actionsOpen && actionsRef.current && !actionsRef.current.contains(event.target)) {
        setActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [columnsOpen, actionsOpen]);

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;
  const selectedCount = selected.size;
  const allSelected = records.length > 0 && records.every((row) => selected.has(row.id));
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleSort = (field) => {
    setOffset(0);
    if (order === field) {
      setOrderDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOrder(field);
    setOrderDir(field === 'name' ? 'asc' : 'asc');
  };

  const toggleSelectAll = (event) => {
    event.stopPropagation();
    if (allSelected) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(records.map((row) => row.id)));
  };

  const toggleSelectOne = (event, id) => {
    event.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openStudent = (id) => {
    navigate(`/bu-dashboard/postgraduate/students/${id}`);
  };

  const toggleActive = async (event, row) => {
    event.stopPropagation();
    if (togglingId) return;
    const nextActive = !row.active;
    setTogglingId(row.id);
    const { error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      postgraduateAPI.archiveStudent(row.id, nextActive, token)
    );
    setTogglingId(null);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    const matchesFilter =
      (filterParams.active_state === 'inactive' && !nextActive) ||
      (filterParams.active_state !== 'inactive' && nextActive);
    if (!matchesFilter) {
      setRecords((prev) => prev.filter((item) => item.id !== row.id));
      setTotal((n) => Math.max(0, n - 1));
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      return;
    }
    setRecords((prev) =>
      prev.map((item) => (item.id === row.id ? { ...item, active: nextActive } : item))
    );
  };

  const openBulkAction = (action) => {
    setActionsOpen(false);
    setBulkAction(action);
  };

  const confirmBulkAction = async () => {
    if (!bulkAction || bulkBusy || selectedCount === 0) return;
    const nextActive = bulkAction === 'unarchive';
    setBulkBusy(true);
    setError(null);
    const { error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      postgraduateAPI.archiveStudents([...selected], nextActive, token)
    );
    setBulkBusy(false);
    if (fetchError) {
      setError(fetchError);
      return;
    }
    setBulkAction(null);
    await loadList();
  };

  const sortIcon = (field) => {
    if (order !== field) return null;
    return orderDir === 'asc' ? (
      <MdArrowUpward size={13} aria-hidden />
    ) : (
      <MdArrowDownward size={13} aria-hidden />
    );
  };

  const colCount =
    5 + Number(visibleCols.batch) + Number(visibleCols.faculty) + Number(visibleCols.department) + 1;

  return (
    <div className="ea-odoo-form-page ea-adm-form ea-ug-view">
      <div className="ea-odoo-form-bar ea-adm-cp">
        <div className="ea-adm-cp__left">
          <div className="ea-adm-cp__title-block">
            <Link to={REPORT_PATH} className="ea-adm-cp__parent">
              PG Students List Report
            </Link>
            <div className="ea-adm-cp__record-row">
              <span className="ea-adm-cp__record">{listTitle}</span>
              <button type="button" className="ea-adm-cp__icon" title="Actions" aria-label="Actions">
                <MdSettings size={16} />
              </button>
            </div>
          </div>
        </div>

        {selectedCount > 0 ? (
          <div className="ea-ug-view__selection">
            <span className="ea-ug-view__sel-count">{selectedCount} selected</span>
            <button
              type="button"
              className="ea-ug-view__sel-clear"
              title="Clear selection"
              aria-label="Clear selection"
              onClick={() => setSelected(new Set())}
            >
              <MdClose size={16} />
            </button>
            <div className="ea-adm-actions" ref={actionsRef}>
              <button
                type="button"
                className="ea-ug-view__actions-btn"
                aria-haspopup="menu"
                aria-expanded={actionsOpen}
                onClick={() => setActionsOpen((v) => !v)}
                disabled={bulkBusy}
              >
                Actions
                <MdExpandMore size={18} aria-hidden />
              </button>
              {actionsOpen ? (
                <div className="ea-adm-actions__menu" role="menu">
                  <button
                    type="button"
                    className="ea-adm-actions__item"
                    role="menuitem"
                    onClick={() => openBulkAction('archive')}
                  >
                    <MdArchive size={16} aria-hidden />
                    Archive
                  </button>
                  <button
                    type="button"
                    className="ea-adm-actions__item"
                    role="menuitem"
                    onClick={() => openBulkAction('unarchive')}
                  >
                    <MdUnarchive size={16} aria-hidden />
                    Unarchive
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="ea-odoo-search ea-ug-view__search">
            <MdSearch className="ea-odoo-search__icon" aria-hidden />
            <input
              type="search"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search students"
            />
            <MdExpandMore className="ea-ug-view__search-caret" aria-hidden />
          </div>
        )}

        <div className="ea-adm-cp__right">
          <div className="ea-odoo-pager ea-adm-cp__pager" aria-label="Pagination">
            <span>{loading ? '…' : `${rangeStart}-${rangeEnd} / ${formatCount(total)}`}</span>
            <div className="ea-adm-cp__pager-btns">
              <button
                type="button"
                className="ea-odoo-pager__btn"
                disabled={!canPrev || loading}
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                aria-label="Previous page"
              >
                <MdChevronLeft size={20} />
              </button>
              <button
                type="button"
                className="ea-odoo-pager__btn"
                disabled={!canNext || loading}
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                aria-label="Next page"
              >
                <MdChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <div className="ea-toast error show ea-dash-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="ea-odoo-list-wrap ea-ug-view__table">
        <table className="ea-odoo-list ea-ug-view__list">
          <thead>
            <tr>
              <th className="ea-ug-view__check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </th>
              <th>
                <button
                  type="button"
                  className={`ea-ug-view__sort${order === 'student_no' ? ' is-active' : ''}`}
                  onClick={() => toggleSort('student_no')}
                >
                  Student ID
                  {sortIcon('student_no')}
                </button>
              </th>
              <th>
                <button
                  type="button"
                  className={`ea-ug-view__sort${order === 'name' ? ' is-active' : ''}`}
                  onClick={() => toggleSort('name')}
                >
                  Full Name
                  {sortIcon('name')}
                </button>
              </th>
              <th>Active</th>
              <th>Status</th>
              {visibleCols.batch ? <th>Batch</th> : null}
              {visibleCols.faculty ? <th>Faculty</th> : null}
              {visibleCols.department ? <th>Department</th> : null}
              <th className="ea-ug-view__cols">
                <div className="ea-ug-view__cols-wrap" ref={columnsRef}>
                  <button
                    type="button"
                    className="ea-adm-cp__icon"
                    title="Optional columns"
                    aria-label="Optional columns"
                    aria-expanded={columnsOpen}
                    onClick={() => setColumnsOpen((v) => !v)}
                  >
                    <MdViewColumn size={16} />
                  </button>
                  {columnsOpen ? (
                    <div className="ea-ug-view__cols-menu" role="menu">
                      {['batch', 'faculty', 'department'].map((key) => (
                        <label key={key} className="ea-ug-view__cols-item">
                          <input
                            type="checkbox"
                            checked={visibleCols[key]}
                            onChange={() =>
                              setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))
                            }
                          />
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && records.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="ea-hr-table__empty">
                  Loading students…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="ea-hr-table__empty">
                  No students found.
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr
                  key={row.id}
                  className={`ea-odoo-att-list__row${row.active ? '' : ' ea-odoo-list__row--muted'}${
                    selected.has(row.id) ? ' is-selected' : ''
                  }`}
                  onClick={() => openStudent(row.id)}
                >
                  <td className="ea-ug-view__check" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={(e) => toggleSelectOne(e, row.id)}
                      aria-label={`Select ${row.name}`}
                    />
                  </td>
                  <td>{row.student_no || ''}</td>
                  <td className="ea-odoo-list__name">{row.name}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`ea-bool-toggle${row.active ? ' is-on' : ''}`}
                      role="switch"
                      aria-checked={row.active}
                      aria-label={row.active ? 'Active' : 'Archived'}
                      disabled={togglingId === row.id}
                      onClick={(e) => toggleActive(e, row)}
                    />
                  </td>
                  <td>
                    <span className={`ea-adm-state ${stateClass(row.state)}`}>
                      {row.state_label || row.state || ''}
                    </span>
                  </td>
                  {visibleCols.batch ? <td>{row.batch_name || ''}</td> : null}
                  {visibleCols.faculty ? <td>{row.faculty_name || ''}</td> : null}
                  {visibleCols.department ? <td>{row.department_name || ''}</td> : null}
                  <td />
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {bulkAction ? (
        <div
          className="ea-odoo-modal-backdrop"
          role="presentation"
          onClick={() => !bulkBusy && setBulkAction(null)}
        >
          <div
            className="ea-odoo-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ea-ug-view-bulk-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ea-odoo-modal__header">
              <h2 id="ea-ug-view-bulk-title">Confirmation</h2>
              <button
                type="button"
                className="ea-odoo-modal__close"
                aria-label="Close"
                disabled={bulkBusy}
                onClick={() => setBulkAction(null)}
              >
                ×
              </button>
            </div>
            <div className="ea-odoo-modal__body">
              <p>
                {bulkAction === 'unarchive'
                  ? selectedCount === 1
                    ? 'Are you sure that you want to unarchive this record?'
                    : `Are you sure that you want to unarchive these ${selectedCount} records?`
                  : selectedCount === 1
                    ? 'Are you sure that you want to archive this record?'
                    : `Are you sure that you want to archive these ${selectedCount} records?`}
              </p>
            </div>
            <div className="ea-odoo-modal__footer">
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--danger"
                disabled={bulkBusy}
                onClick={confirmBulkAction}
              >
                {bulkBusy
                  ? bulkAction === 'unarchive'
                    ? 'Unarchiving…'
                    : 'Archiving…'
                  : bulkAction === 'unarchive'
                    ? 'Unarchive'
                    : 'Archive'}
              </button>
              <button
                type="button"
                className="ea-odoo-btn ea-odoo-btn--ghost"
                disabled={bulkBusy}
                onClick={() => setBulkAction(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
